import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import CreditCardDisplay from '../components/common/CreditCardDisplay';
import AddCardDialog from '../components/common/AddCardDialog';
import AddTransactionDialog from '../components/common/AddTransactionDialog';
import BulkTransactionUpload from '../components/common/BulkTransactionUpload';
import QuickEntryMode from '../components/common/QuickEntryMode';
import TransactionHistoryDialog from '../components/common/TransactionHistoryDialog';
import SpendingOptimizer from '../components/common/SpendingOptimizer';
import { styled } from '@mui/material/styles';
import {
  CreditCard,
  Add,
  Star,
  TrendingUp,
  AccountBalanceWallet,
  SwapHoriz,
  ArrowForward,
  LocalOffer,
  Security,
  Percent,
  Receipt,
  UploadFile,
  Bolt,
  KeyboardArrowDown,
} from '@mui/icons-material';
import PersistenceService from '../services/persistenceService';
import FirestoreService from '../services/firestoreService';
import { realDataService } from '../services/realDataService';
import { userDataService } from '../services/userDataService';
import realCardOffersService, { PersonalizedOffer } from '../services/realCardOffersService';
import { notificationService } from '../services/notificationService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { LoyaltyProgram, Transaction } from '../types';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'}`,
  background: theme.palette.mode === 'light' 
    ? 'rgba(255, 255, 255, 0.8)'
    : 'rgba(15, 23, 42, 0.8)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 12px 30px rgba(0, 0, 0, 0.1)'
      : '0 12px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const OfferCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  background: theme.palette.mode === 'light'
    ? 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)'
    : 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)',
  color: 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.02)',
  },
}));

// Removed PremiumCard - now using CreditCardDisplay component

export const Cards: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
  const { showToast } = useNotifications();
  const [cardPrograms, setCardPrograms] = React.useState<LoyaltyProgram[]>([]);
  const [userTransactions, setUserTransactions] = React.useState<Transaction[]>([]);
  const [personalizedOffers, setPersonalizedOffers] = React.useState<PersonalizedOffer[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isTransactionDialogOpen, setIsTransactionDialogOpen] = React.useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = React.useState(false);
  const [isQuickEntryOpen, setIsQuickEntryOpen] = React.useState(false);
  const [transactionMenuAnchor, setTransactionMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [selectedProgramForHistory, setSelectedProgramForHistory] = React.useState<LoyaltyProgram | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [programToDelete, setProgramToDelete] = React.useState<LoyaltyProgram | null>(null);
  const [isTransactionHistoryOpen, setIsTransactionHistoryOpen] = React.useState(false);

  React.useEffect(() => {
    const loadCardPrograms = async () => {
      console.log('═══════════════════════════════════════════');
      console.log('🔄 LOADING CARD PROGRAMS - START');
      console.log('═══════════════════════════════════════════');
      console.log('👤 User:', user ? { id: user.id, email: user.email } : 'null');
      console.log('⏳ Auth loading:', authLoading);
      
      try {
        // Debug storage for persistence testing
        PersistenceService.debugAllStorageKeys();
        
        // Load ALL programs
        console.log('📥 Calling PersistenceService.getUserLoyaltyPrograms...');
        const programs = await PersistenceService.getUserLoyaltyPrograms(user);
        console.log('📦 Received programs:', programs.length, 'total');
        console.log('📋 Program details:', programs.map(p => ({ 
          id: p.id, 
          name: p.name, 
          type: p.type,
          points: p.points 
        })));
        
        // Filter only credit card programs
        const creditCards = programs.filter(p => p.type === 'credit_card');
        console.log('💳 Filtered credit cards:', creditCards.length);
        console.log('💳 Credit card details:', creditCards.map(c => ({ 
          id: c.id, 
          name: c.name, 
          type: c.type,
          cardType: c.cardType,
          points: c.points 
        })));
        
        setCardPrograms(creditCards);
        console.log('✅ State updated with', creditCards.length, 'credit cards');
        console.log('═══════════════════════════════════════════');
        console.log('🔄 LOADING CARD PROGRAMS - END');
        console.log('═══════════════════════════════════════════');

        // Load user transactions for spending optimization
        try {
          if (user?.id) {
            console.log('📥 Loading transactions for user:', user.id);
            const transactions = await realDataService.getTransactions(user.id);
            console.log('✅ Loaded transactions:', transactions.length);
            console.log('📋 Transaction details:', transactions.slice(0, 3).map(t => ({
              id: t.id,
              description: t.description,
              points: t.points,
              date: t.date,
              programId: t.programId
            })));
            setUserTransactions(transactions);
          } else {
            console.log('⚠️ No user ID, skipping transaction load');
            setUserTransactions([]);
          }
        } catch (transactionError) {
          console.error('❌ Could not load transactions:', transactionError);
          setUserTransactions([]);
        }

        // Load personalized card offers
        try {
          const offers = await realCardOffersService.getPersonalizedOffers(programs, 3000);
          console.log('Loaded personalized offers:', offers.length);
          setPersonalizedOffers(offers);
        } catch (offersError) {
          console.warn('Could not load personalized offers:', offersError);
          setPersonalizedOffers([]);
        }
      } catch (error) {
        console.error('❌ ERROR loading card programs:', error);
        setCardPrograms([]);
      } finally {
        if (!authLoading) {
          setLoading(false);
        }
      }
    };

    // Only load data when auth state is determined (not loading)
    if (!authLoading) {
      // Add a small delay to ensure auth state is fully settled
      const timer = setTimeout(() => {
        loadCardPrograms();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, authLoading]);

  const handleAddCard = async (newCardData: Omit<LoyaltyProgram, 'id'>) => {
    console.log('═══════════════════════════════════════════');
    console.log('💾 ADDING NEW CARD - START');
    console.log('═══════════════════════════════════════════');
    console.log('📄 Card data received:', newCardData);
    console.log('👤 Current user:', user ? { id: user.id, email: user.email } : 'null');
    
    try {
      // Use the new persistence service for robust data saving
      console.log('💾 Calling PersistenceService.addUserLoyaltyProgram...');
      const savedCard = await PersistenceService.addUserLoyaltyProgram(user, newCardData);
      console.log('✅ Card saved successfully with ID:', savedCard.id);
      console.log('📋 Saved card details:', savedCard);
      
      // Update local state
      setCardPrograms(prev => {
        const updated = [...prev, savedCard];
        console.log('📊 Updated local state. Total cards now:', updated.length);
        console.log('📋 All cards:', updated.map(c => ({ id: c.id, name: c.name, type: c.type })));
        return updated;
      });
      
      // Show success notification
      showToast(`${newCardData.name} added successfully!`, 'success');
      
      // Trigger persistent database notification for card addition
      if (user?.id) {
        await notificationService.addNotification(user.id, {
          type: 'sync_success',
          priority: 'low',
          title: `${newCardData.name} Added`,
          message: `You've successfully added ${newCardData.name} with ${newCardData.points.toLocaleString()} points`,
          actionUrl: '/cards',
          actionLabel: 'View Card',
          metadata: {
            programId: savedCard.id,
            programName: newCardData.name,
            points: newCardData.points,
          },
          dismissible: true,
          persistent: true,
        });
      }
      
      // Verify the save by reloading from storage
      console.log('🔍 Verifying save by reloading...');
      const verifyPrograms = await PersistenceService.getUserLoyaltyPrograms(user);
      const verifyCreditCards = verifyPrograms.filter(p => p.type === 'credit_card');
      console.log('🔍 Verification: Found', verifyCreditCards.length, 'credit cards after save');
      console.log('🔍 Verification cards:', verifyCreditCards.map(c => ({ id: c.id, name: c.name })));
      
      console.log('═══════════════════════════════════════════');
      console.log('💾 ADDING NEW CARD - END (SUCCESS)');
      console.log('═══════════════════════════════════════════');
    } catch (error) {
      console.error('═══════════════════════════════════════════');
      console.error('❌ ADDING NEW CARD - END (ERROR)');
      console.error('❌ Error details:', error);
      console.error('═══════════════════════════════════════════');
      showToast('Failed to add card. Please try again.', 'error');
    }
  };

    const handleDeleteCard = async (cardToDelete: LoyaltyProgram) => {
    // Show confirmation dialog
    setProgramToDelete(cardToDelete);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!user || !programToDelete) return;
    
    try {
      // Remove from Firestore
      await FirestoreService.deleteLoyaltyProgram(programToDelete.id);
      
      // Update local state
      setCardPrograms(prev => prev.filter(card => card.id !== programToDelete.id));
      console.log('Card deleted successfully:', programToDelete.name);
      
      // Show success notification
      showToast(`${programToDelete.name} removed successfully`, 'success');
      
      // Trigger persistent database notification for card deletion
      if (user?.id) {
        await notificationService.addNotification(user.id, {
          type: 'sync_success',
          priority: 'low',
          title: `${programToDelete.name} Removed`,
          message: `${programToDelete.name} has been removed from your loyalty programs`,
          actionUrl: '/cards',
          actionLabel: 'View Cards',
          dismissible: true,
        });
      }
      
      // Close dialog and reset
      setDeleteConfirmOpen(false);
      setProgramToDelete(null);
    } catch (error) {
      console.error('Error deleting card:', error);
      showToast('Failed to remove card. Please try again.', 'error');
      alert(`Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleAddTransaction = async (transactionData: Omit<Transaction, 'id'>) => {
    if (!user?.id) return;
    
    console.log('═══════════════════════════════════════════');
    console.log('💾 ADDING TRANSACTION - START');
    console.log('═══════════════════════════════════════════');
    console.log('👤 User ID:', user.id);
    console.log('📦 Transaction data:', {
      programId: transactionData.programId,
      description: transactionData.description,
      points: transactionData.points,
      type: transactionData.type,
      date: transactionData.date,
      balance: transactionData.balance
    });
    
    try {
      // Add to Firebase
      const transactionId = await userDataService.addUserTransaction(user.id, transactionData);
      console.log('✅ Transaction added successfully with ID:', transactionId);
      
      // Reload transactions from database to ensure consistency
      try {
        console.log('🔄 Reloading transactions from database...');
        const updatedTransactions = await realDataService.getTransactions(user.id);
        console.log('✅ Reloaded', updatedTransactions.length, 'transactions');
        console.log('📋 First 3 transactions:', updatedTransactions.slice(0, 3).map(t => ({
          id: t.id,
          programId: t.programId,
          description: t.description,
          points: t.points,
          type: t.type
        })));
        setUserTransactions(updatedTransactions);
      } catch (error) {
        console.error('❌ Could not reload transactions:', error);
      }
      
      // Update card points balance
      const updatedProgram = cardPrograms.find(card => card.id === transactionData.programId);
      setCardPrograms(prev => prev.map(card => 
        card.id === transactionData.programId 
          ? { ...card, points: transactionData.balance }
          : card
      ));
      
      // Show success notification with details
      if (updatedProgram) {
        const isEarned = transactionData.points > 0;
        showToast(
          `${isEarned ? 'Earned' : 'Redeemed'} ${Math.abs(transactionData.points).toLocaleString()} points ${isEarned ? 'to' : 'from'} ${updatedProgram.name}`,
          'success'
        );
        
        // Trigger persistent database notification for transaction
        await notificationService.notifyTransaction(
          user.id,
          updatedProgram.name,
          transactionData.points,
          isEarned ? 'earned' : 'redeemed'
        );
        
        // Check for milestones (every 10,000 points)
        const milestones = [10000, 25000, 50000, 75000, 100000, 150000, 200000];
        const previousPoints = transactionData.balance - transactionData.points;
        const passedMilestone = milestones.find(m => previousPoints < m && transactionData.balance >= m);
        
        if (passedMilestone && isEarned) {
          setTimeout(() => {
            showToast(`🎉 Milestone! You've reached ${passedMilestone.toLocaleString()} points in ${updatedProgram.name}!`, 'success');
            // Trigger persistent milestone notification
            notificationService.notifyMilestone(
              user.id,
              updatedProgram.name,
              passedMilestone,
              transactionData.balance
            );
          }, 1500);
        }
      }
      
      // Reload personalized offers with updated data
      try {
        const offers = await realCardOffersService.getPersonalizedOffers(cardPrograms, 3000);
        setPersonalizedOffers(offers);
      } catch (offersError) {
        console.warn('Could not reload personalized offers:', offersError);
      }
      
      console.log('═══════════════════════════════════════════');
      console.log('💾 ADDING TRANSACTION - END (SUCCESS)');
      console.log('═══════════════════════════════════════════');
      
    } catch (error) {
      console.error('═══════════════════════════════════════════');
      console.error('❌ ADDING TRANSACTION - END (ERROR)');
      console.error('❌ Error details:', error);
      console.error('═══════════════════════════════════════════');
    }
  };

  const handleBulkUpload = async (transactions: Omit<Transaction, 'id'>[]) => {
    if (!user?.id) return;
    
    console.log('═══════════════════════════════════════════');
    console.log('📤 BULK UPLOAD - START');
    console.log('═══════════════════════════════════════════');
    console.log('👤 User ID:', user.id);
    console.log('📊 Transactions to upload:', transactions.length);
    
    try {
      let successCount = 0;
      let errorCount = 0;
      const addedTransactionIds: string[] = [];

      for (const txn of transactions) {
        try {
          console.log('💾 Saving transaction:', txn.description, txn.points, 'pts');
          const transactionId = await userDataService.addUserTransaction(user.id, txn);
          console.log('✅ Saved with ID:', transactionId);
          addedTransactionIds.push(transactionId);
          successCount++;
          
          // Update card points balance
          setCardPrograms(prev => prev.map(card => 
            card.id === txn.programId 
              ? { ...card, points: txn.balance }
              : card
          ));
        } catch (error) {
          console.error('❌ Error adding transaction:', error);
          errorCount++;
        }
      }

      console.log('📊 Upload complete. Success:', successCount, 'Errors:', errorCount);

      // Reload transactions from database to ensure consistency
      if (successCount > 0 && user?.id) {
        try {
          console.log('🔄 Reloading transactions from database...');
          const updatedTransactions = await realDataService.getTransactions(user.id);
          console.log('✅ Reloaded', updatedTransactions.length, 'transactions');
          setUserTransactions(updatedTransactions);
        } catch (error) {
          console.error('❌ Could not reload transactions:', error);
        }
      }

      if (successCount > 0) {
        showToast(`Successfully uploaded ${successCount} transaction${successCount !== 1 ? 's' : ''}!`, 'success');
      }
      if (errorCount > 0) {
        showToast(`Failed to upload ${errorCount} transaction${errorCount !== 1 ? 's' : ''}`, 'error');
      }

      // Reload personalized offers
      try {
        const offers = await realCardOffersService.getPersonalizedOffers(cardPrograms, 3000);
        setPersonalizedOffers(offers);
      } catch (error) {
        console.warn('Could not reload offers:', error);
      }
      
      console.log('═══════════════════════════════════════════');
      console.log('📤 BULK UPLOAD - END');
      console.log('═══════════════════════════════════════════');
    } catch (error) {
      console.error('Error in bulk upload:', error);
      showToast('Failed to upload transactions', 'error');
    }
  };

  const handleQuickEntry = async (transactions: Omit<Transaction, 'id'>[]) => {
    await handleBulkUpload(transactions);
  };

  const totalPoints = cardPrograms.reduce((sum: number, program: LoyaltyProgram) => sum + program.points, 0);
  const activeCards = cardPrograms.length;

  // Use real personalized offers instead of static data

  const transferPartners = [
    { from: 'Chase Sapphire', to: 'United Airlines', ratio: '1:1', bonus: '25%' },
    { from: 'HDFC Infinia', to: 'Singapore Airlines', ratio: '1:1', bonus: '20%' },
    { from: 'Axis Magnus', to: 'Emirates', ratio: '1:1', bonus: '30%' },
    { from: 'American Express', to: 'Marriott Bonvoy', ratio: '1:1', bonus: '15%' },
  ];

  const cardBenefits = [
    {
      category: 'Travel Perks',
      benefits: ['Airport Lounge Access', 'Travel Insurance', 'Concierge Service', 'Priority Check-in'],
    },
    {
      category: 'Earning Rates',
      benefits: ['5X on Travel', '3X on Dining', '2X on Online Shopping', '1X on Everything Else'],
    },
    {
      category: 'Protection',
      benefits: ['Purchase Protection', 'Extended Warranty', 'Price Protection', 'Fraud Monitoring'],
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <LinearProgress sx={{ width: 200, mb: 2 }} />
            <Typography>Loading credit card programs...</Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, mb: 1, color: 'text.primary' }}>
          Credit Cards Hub
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 3 }}>
          Manage your credit card rewards and maximize earning potential
        </Typography>

        {/* Summary Cards */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 4 
        }}>
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <CreditCard sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {totalPoints.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Rewards Points
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Across all cards
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <AccountBalanceWallet sx={{ fontSize: 48, color: '#f59e0b', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {activeCards}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Cards
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Connected accounts
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard>
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <TrendingUp sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                3.2X
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Average Multiplier
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Points per rupee
              </Typography>
            </CardContent>
          </StyledCard>
          
          <StyledCard 
            sx={{ 
              background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: (theme) => theme.shadows[8]
              }
            }}
            onClick={() => setIsAddDialogOpen(true)}
          >
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
              <Add sx={{ fontSize: 48, color: 'white', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Add Card
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Connect new card
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }} display="block">
                Track more rewards
              </Typography>
            </CardContent>
          </StyledCard>
        </Box>
      </Box>

      {/* Spending Optimization Engine */}
      <SpendingOptimizer userCards={cardPrograms} userTransactions={userTransactions} />

      {/* Card Offers */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalOffer color="primary" />
          Personalized Card Recommendations
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 2 
        }}>
          {personalizedOffers.slice(0, 6).map((personalizedOffer, index) => {
            const { offer } = personalizedOffer;
            return (
              <OfferCard key={offer.id}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {offer.bank} {offer.cardName}
                    </Typography>
                    {personalizedOffer.matchScore >= 80 && (
                      <Chip 
                        label="PERFECT MATCH" 
                        size="small" 
                        sx={{ 
                          backgroundColor: '#10b981', 
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.6rem'
                        }} 
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
                    {offer.offer}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                    {offer.requirement}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Estimated Value:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{(offer.estimatedValue * 88.62).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Best Category:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{offer.category}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Annual Fee:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {offer.annualFee === 0 ? 'FREE' : `₹${(offer.annualFee * 88.62).toLocaleString()}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Match Score:</Typography>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 600,
                        color: personalizedOffer.matchScore >= 70 ? '#10b981' : 
                               personalizedOffer.matchScore >= 50 ? '#f59e0b' : '#ef4444'
                      }}>
                        {personalizedOffer.matchScore}%
                      </Typography>
                    </Box>
                  </Box>

                  {personalizedOffer.reasonToApply && (
                    <Typography variant="caption" sx={{ 
                      display: 'block', 
                      mb: 2, 
                      fontStyle: 'italic',
                      opacity: 0.8 
                    }}>
                      💡 {personalizedOffer.reasonToApply}
                    </Typography>
                  )}

                  {offer.isLimitedTime && (
                    <Chip 
                      label="LIMITED TIME" 
                      size="small" 
                      sx={{ 
                        backgroundColor: '#f59e0b', 
                        color: 'white',
                        fontWeight: 600,
                        mb: 2,
                        animation: 'pulse 2s infinite'
                      }} 
                    />
                  )}

                  <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward />}
                    sx={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    },
                  }}
                >
                  Apply Now
                </Button>
              </CardContent>
            </OfferCard>
            );
          })}
        </Box>
      </Box>

      {/* Your Credit Cards */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Your Credit Cards
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              endIcon={<KeyboardArrowDown />}
              onClick={(e) => setTransactionMenuAnchor(e.currentTarget)}
              disabled={cardPrograms.length === 0}
              sx={{
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: 'rgba(16, 185, 129, 0.04)',
                },
              }}
            >
              Add Transaction
            </Button>
            <Menu
              anchorEl={transactionMenuAnchor}
              open={Boolean(transactionMenuAnchor)}
              onClose={() => setTransactionMenuAnchor(null)}
            >
              <MenuItem onClick={() => {
                setTransactionMenuAnchor(null);
                setIsTransactionDialogOpen(true);
              }}>
                <ListItemIcon>
                  <Receipt fontSize="small" />
                </ListItemIcon>
                <ListItemText>Single Transaction</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => {
                setTransactionMenuAnchor(null);
                setIsQuickEntryOpen(true);
              }}>
                <ListItemIcon>
                  <Bolt fontSize="small" />
                </ListItemIcon>
                <ListItemText>Quick Entry Mode</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => {
                setTransactionMenuAnchor(null);
                setIsBulkUploadOpen(true);
              }}>
                <ListItemIcon>
                  <UploadFile fontSize="small" />
                </ListItemIcon>
                <ListItemText>Bulk Upload CSV</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 3 
        }}>
          {cardPrograms.length === 0 ? (
            <Box sx={{ 
              gridColumn: '1 / -1',
              textAlign: 'center',
              py: 8,
              px: 3,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 3,
              backgroundColor: 'background.paper',
            }}>
              <CreditCard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                No Credit Cards Added
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                Add your first credit card to start tracking rewards, analyzing spending patterns, and getting personalized recommendations.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => setIsAddDialogOpen(true)}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                  }}
                >
                  Add Your First Card
                </Button>
              </Box>
            </Box>
          ) : (
            cardPrograms.map((program: LoyaltyProgram, index: number) => {
            const cardType = program.tier.toLowerCase().includes('platinum') ? 'platinum' 
                           : program.tier.toLowerCase().includes('gold') ? 'gold' : 'premium';

            return (
              <CreditCardDisplay 
                key={program.id} 
                program={program} 
                cardType={cardType}
                onDelete={handleDeleteCard}
                showDeleteButton={true}
                className="card-container"
                transactions={userTransactions}
                onViewTransactions={() => {
                  console.log('═══════════════════════════════════════════');
                  console.log('🔍 VIEW TRANSACTIONS CLICKED');
                  console.log('═══════════════════════════════════════════');
                  console.log('📊 Program:', program.name, '(ID:', program.id + ')');
                  console.log('📦 Total transactions available:', userTransactions.length);
                  console.log('📋 Transactions for this program:', userTransactions.filter(t => t.programId === program.id).length);
                  setSelectedProgramForHistory(program);
                  setIsTransactionHistoryOpen(true);
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 0.5,
                  minWidth: 0, // Allow text truncation
                }}>
                  {/* Points Display */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'baseline',
                    gap: 0.5,
                    mb: 0.5,
                  }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      color: 'white',
                      fontSize: '1rem',
                      textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                      lineHeight: 1,
                    }}>
                      {program.points.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      fontSize: '0.7rem',
                      textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                      textTransform: 'uppercase',
                      fontWeight: 600,
                    }}>
                      {program.currency}
                    </Typography>
                  </Box>

                  {/* Tier Badge */}
                  <Chip 
                    label={program.tier} 
                    size="small"
                    sx={{ 
                      background: 'rgba(255, 255, 255, 0.15)', 
                      backdropFilter: 'blur(4px)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 18,
                      width: 'fit-content',
                      textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                      '& .MuiChip-label': {
                        px: 0.8,
                      }
                    }}
                  />
                </Box>

                {/* Progress Bar - Appears in vertical middle on hover */}
                <Box sx={{ 
                  opacity: 0,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '.card-container:hover &': {
                    opacity: 1,
                  },
                  borderRadius: 1.5,
                  p: 1.5,
                  textAlign: 'left',
                }}>
                  <Typography variant="caption" sx={{ 
                    color: 'rgba(255, 255, 255, 0.9) !important',
                    fontSize: '0.65rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    fontWeight: 600,
                    mb: 0.5,
                    display: 'block',
                  }}>
                    Tier Progress
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min(75 + index * 10, 95)} 
                    sx={{ 
                      borderRadius: 1, 
                      height: 3, 
                      mb: 0.5,
                      boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        borderRadius: 1,
                      },
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    }}
                  />
                  <Typography variant="caption" sx={{ 
                    color: 'rgba(255, 255, 255, 0.9) !important', 
                    fontSize: '0.6rem',
                    textShadow: '0 1px 2px rgba(0,0,0,0.8)',
                    fontWeight: 500,
                  }}>
                    {Math.min(75 + index * 10, 95)}% to next tier
                  </Typography>
                </Box>
              </CreditCardDisplay>
            );
          }))}
        </Box>
      </Box>

      {/* Transfer Partners */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHoriz color="primary" />
          Transfer Partners
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: 2 
        }}>
          {transferPartners.map((partner, index) => (
            <StyledCard key={index}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {partner.from} → {partner.to}
                  </Typography>
                  <Chip 
                    label={`+${partner.bonus} Bonus`} 
                    size="small" 
                    color="success"
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Transfer Ratio
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {partner.ratio}
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="outlined"
                  endIcon={<ArrowForward />}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                >
                  Transfer Points
                </Button>
              </CardContent>
            </StyledCard>
          ))}
        </Box>
      </Box>

      {/* Card Benefits */}
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Security color="primary" />
          Premium Benefits
        </Typography>
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3 
        }}>
          {cardBenefits.map((benefit, index) => (
            <StyledCard key={index}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: index === 0 
                      ? 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                      : index === 1
                      ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                      : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {index === 0 ? <Star sx={{ color: 'white', fontSize: 20 }} /> :
                     index === 1 ? <Percent sx={{ color: 'white', fontSize: 20 }} /> :
                     <Security sx={{ color: 'white', fontSize: 20 }} />}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {benefit.category}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {benefit.benefits.map((perk, perkIndex) => (
                    <Box key={perkIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 6, 
                        height: 6, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main' 
                      }} />
                      <Typography variant="body2">
                        {perk}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </StyledCard>
          ))}
        </Box>
      </Box>

      {/* Add Card Dialog */}
      <AddCardDialog
        open={isAddDialogOpen}
        onClose={() => setIsAddDialogOpen(false)}
        onAddCard={handleAddCard}
      />

      {/* Add Transaction Dialog */}
      <AddTransactionDialog
        open={isTransactionDialogOpen}
        onClose={() => setIsTransactionDialogOpen(false)}
        onAddTransaction={handleAddTransaction}
        userCards={cardPrograms}
      />

      {/* Bulk Upload Dialog */}
      <BulkTransactionUpload
        open={isBulkUploadOpen}
        onClose={() => setIsBulkUploadOpen(false)}
        onUpload={handleBulkUpload}
        programs={cardPrograms}
      />

      {/* Quick Entry Mode Dialog */}
      <QuickEntryMode
        open={isQuickEntryOpen}
        onClose={() => setIsQuickEntryOpen(false)}
        onSave={handleQuickEntry}
        programs={cardPrograms}
      />

      {/* Transaction History Dialog */}
      {selectedProgramForHistory && (
        <TransactionHistoryDialog
          open={isTransactionHistoryOpen}
          onClose={() => {
            setIsTransactionHistoryOpen(false);
            setSelectedProgramForHistory(null);
          }}
          program={selectedProgramForHistory}
          transactions={userTransactions}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Delete Credit Card?
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{programToDelete?.name}</strong>? 
            This action cannot be undone and will permanently remove the card and all associated data.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              setDeleteConfirmOpen(false);
              setProgramToDelete(null);
            }} 
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained" 
            color="error"
            sx={{ ml: 1 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};